import { IScope } from "@/api";
import { styles } from "@/config/project";
import { AutoAwesome } from "@mui/icons-material";
import { alpha, Box, keyframes, Typography, useTheme } from "@mui/material";

// Keyframe animations
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-8px);
  }
`;

const scanLine = keyframes`
  0% {
    top: 0%;
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    top: 100%;
    opacity: 0;
  }
`;

interface ScopeProcessingStateProps {
    scope: IScope;
}

export const ScopeProcessingState = ({ scope }: ScopeProcessingStateProps) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                overflow: "auto",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: { xs: 2, sm: 3, md: 4 },
                    maxWidth: 480,
                    width: "100%",
                    px: 4,
                    py: { xs: 3, sm: 4, md: 6 },
                    my: "auto",
                }}
            >
                {/* Animated document illustration */}
                <Box
                    sx={{
                        position: "relative",
                        width: { xs: 80, sm: 100, md: 120 },
                        height: { xs: 100, sm: 125, md: 150 },
                        flexShrink: 0,
                        animation: `${float} 3s ease-in-out infinite`,
                    }}
                >
                    {/* Document shape */}
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            bgcolor: "white",
                            borderRadius: 2,
                            boxShadow: `0 20px 40px ${alpha(styles.primaryColor, 0.15)}, 0 0 0 1px ${alpha(styles.primaryColor, 0.1)}`,
                            overflow: "hidden",
                        }}
                    >
                        {/* Document content lines */}
                        {[...Array(8)].map((_, i) => (
                            <Box
                                key={i}
                                sx={{
                                    position: "absolute",
                                    left: 16,
                                    right: i % 3 === 0 ? 40 : 16,
                                    top: 20 + i * 14,
                                    height: 8,
                                    borderRadius: 1,
                                    bgcolor: theme.palette.grey[200],
                                }}
                            />
                        ))}

                        {/* Scanning line */}
                        <Box
                            sx={{
                                position: "absolute",
                                left: 0,
                                right: 0,
                                height: 3,
                                background: `linear-gradient(90deg, transparent, ${styles.primaryColor}, transparent)`,
                                animation: `${scanLine} 2s ease-in-out infinite`,
                                boxShadow: `0 0 20px ${styles.primaryColor}`,
                            }}
                        />
                    </Box>

                    {/* Decorative elements */}
                    <Box
                        sx={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            bgcolor: styles.primaryColor,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: `0 4px 12px ${alpha(styles.primaryColor, 0.4)}`,
                        }}
                    >
                        <AutoAwesome sx={{ color: "white", fontSize: 14 }} />
                    </Box>
                </Box>

                {/* Title and description */}
                <Box sx={{ textAlign: "center", flexShrink: 0 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 600,
                            color: "text.primary",
                            mb: 0.5,
                            fontSize: {
                                xs: "1rem",
                                sm: "1.125rem",
                                md: "1.25rem",
                            },
                        }}
                    >
                        Scoping your document
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: "text.secondary",
                            maxWidth: 360,
                            mx: "auto",
                            lineHeight: 1.5,
                        }}
                    >
                        {scope.document_title}
                    </Typography>
                </Box>

                {/* Shimmer bar */}
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: { xs: 160, sm: 180, md: 200 },
                        height: { xs: 3, sm: 4 },
                        borderRadius: 2,
                        bgcolor: alpha(styles.primaryColor, 0.1),
                        overflow: "hidden",
                        flexShrink: 0,
                    }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            background: `linear-gradient(90deg, transparent, ${styles.primaryColor}, transparent)`,
                            backgroundSize: "200% 100%",
                            animation: `${shimmer} 1.5s linear infinite`,
                        }}
                    />
                </Box>

                {/* Helpful tip */}
                <Typography
                    variant="caption"
                    sx={{
                        color: "text.secondary",
                        textAlign: "center",
                        mt: { xs: 0, sm: 1, md: 2 },
                        flexShrink: 0,
                    }}
                >
                    This usually takes a few moments. You'll be notified when
                    it's ready.
                </Typography>
            </Box>
        </Box>
    );
};
